import { useEffect, useState } from "react";
import axios from "axios";
import { useLanguage, useSEO } from "../context/LanguageContext";
import { PageHero, SectionHeading, Reveal, Overline, CTAButton } from "../components/Kinetic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  idee: "border-graphite/25 text-graphite-soft",
  exploration: "border-science/40 bg-science-soft/60 text-science",
  preparation: "border-forest/40 bg-forest-soft text-forest",
  partenaires: "bg-science text-white border-science",
  developpement: "bg-forest text-white border-forest",
  termine: "bg-graphite text-white border-graphite",
};

const FIELDS = ["problematique", "contexte", "objectif", "partenaires", "besoins", "etapes"];

const ProjetsPage = () => {
  const { t, lang } = useLanguage();
  useSEO(t("projects.metaTitle"), t("projects.metaDesc"));
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    axios.get(`${API}/projects`).then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  const labels = t("projects.labels");
  const statuses = t("projectStatuses");

  return (
    <div data-testid="projets-page">
      <PageHero
        overline={t("projects.overline")}
        titleLines={t("projects.titleLines")}
        intro={t("projects.intro")}
        testid="projets"
      />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-5xl px-6">
          {projects !== null && projects.length === 0 ? (
            <Reveal>
              <div className="border border-graphite/15 bg-white p-10 md:p-16" data-testid="projects-empty-state">
                <Overline>{t("status.prep")}</Overline>
                <h2 className="mt-5 max-w-2xl font-display text-3xl md:text-4xl font-light tracking-tight leading-tight text-graphite">
                  {t("projects.emptyTitle")}
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-graphite-soft">{t("projects.emptyText")}</p>
                <div className="mt-8">
                  <CTAButton to="/programme" testid="projects-cta-propose">
                    {t("projects.cta")}
                  </CTAButton>
                </div>
              </div>
            </Reveal>
          ) : (
            <div className="space-y-8" data-testid="projects-list">
              {(projects || []).map((p, i) => (
                <Reveal key={p.id} delay={i * 0.06}>
                  <article className="border border-graphite/15 bg-white p-8 md:p-12" data-testid={`project-card-${p.id}`}>
                    <span className={`inline-block border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLES[p.statut] || STATUS_STYLES.idee}`} data-testid={`project-status-${p.id}`}>
                      {statuses[p.statut] || p.statut}
                    </span>
                    <h2 className="mt-5 font-display text-2xl md:text-4xl font-light tracking-tight text-graphite">
                      {lang === "en" && p.title_en ? p.title_en : p.title_fr}
                    </h2>
                    <div className="mt-8">
                      {FIELDS.map((f) => {
                        const value = lang === "en" && p[`${f}_en`] ? p[`${f}_en`] : p[`${f}_fr`];
                        if (!value) return null;
                        return (
                          <div key={f} className="grid gap-2 border-t border-graphite/10 py-4 md:grid-cols-4">
                            <p className="overline-mono text-forest">{labels[f]}</p>
                            <p className="text-sm md:text-base leading-relaxed text-graphite-soft md:col-span-3">{value}</p>
                          </div>
                        );
                      })}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          )}

          <Reveal delay={0.15}>
            <div className="mt-20 border-t border-graphite/10 pt-12">
              <p className="overline-mono text-forest">{t("projects.statusesTitle")}</p>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-graphite-soft">{t("projects.statusesText")}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                {Object.entries(statuses).map(([key, label]) => (
                  <span key={key} className={`inline-block border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] ${STATUS_STYLES[key]}`}>
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ProjetsPage;
